/**
 * Procedural texture atelier — canvas-baked felt, wood and noise maps.
 * No external assets: the whole board is generated at runtime, which keeps the
 * bundle light and lets every theme re-bake its own material response.
 * (卷五 · 材质律：一切表面必须可命名 — 毡/木/黄铜)
 */
import * as THREE from 'three';
import { generateBoardPositions, hexToCartesian } from '@shared/utils/hexMath';
import { CAMP_POSITIONS } from '@shared/constants/camps';
import { PLAYER_COLORS, BOARD } from '@shared/constants/board';
import type { ThemePreset } from '@shared/config/theme';

const FELT_TEX_SIZE = 2048;
const WOOD_TEX_SIZE = 1024;

/** World-space half-extent the felt texture covers (hole tips reach 12.0 + margin). */
export const FELT_WORLD_EXTENT = 13.2;

function makeCanvas(size: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  return { canvas, ctx };
}

function worldToTex(v: number, size: number): number {
  const half = FELT_WORLD_EXTENT;
  return ((v + half) / (2 * half)) * size;
}

function hexToColor(hex: string): { r: number; g: number; b: number } {
  const c = new THREE.Color(hex);
  return { r: c.r, g: c.g, b: c.b };
}

/** Bake the felt playfield: weave speckle, drilled holes, camp tints, vignette. */
export function createFeltTexture(theme: ThemePreset): THREE.CanvasTexture {
  const { canvas, ctx } = makeCanvas(FELT_TEX_SIZE);
  const S = FELT_TEX_SIZE;
  const base = hexToColor(theme.felt);
  const deep = hexToColor(theme.feltDeep);

  const to255 = (v: number) => Math.round(v * 255);
  ctx.fillStyle = theme.felt;
  ctx.fillRect(0, 0, S, S);

  // ── Weave speckle (毛毡织纹) via per-pixel jitter ──
  const img = ctx.getImageData(0, 0, S, S);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() - 0.5) * 26;
    d[i] = Math.min(255, Math.max(0, to255(base.r) + n));
    d[i + 1] = Math.min(255, Math.max(0, to255(base.g) + n));
    d[i + 2] = Math.min(255, Math.max(0, to255(base.b) + n));
  }
  ctx.putImageData(img, 0, 0);

  // ── Camp tip tints — faint jewel halos so camps read at a glance ──
  if (theme.campTint) {
    for (let camp = 0; camp < 6; camp++) {
      const tip = CAMP_POSITIONS[camp]?.[0];
      if (!tip) continue;
      const { x, z } = hexToCartesian(tip);
      const cx = worldToTex(x, S);
      const cy = worldToTex(z, S);
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, S * 0.115);
      grad.addColorStop(0, PLAYER_COLORS[camp] + '2E');
      grad.addColorStop(1, PLAYER_COLORS[camp] + '00');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, S, S);
    }
  }

  // ── The 121 drilled holes (孔位凹陷 + 内阴影) ──
  // holeR in texture pixels = world HOLE_RADIUS mapped through worldToTex
  // worldToTex derivative: size / (2 * FELT_WORLD_EXTENT) px per world unit
  const holeR = BOARD.HOLE_RADIUS * (S / (2 * FELT_WORLD_EXTENT));
  for (const pos of generateBoardPositions()) {
    const { x, z } = hexToCartesian(pos);
    const cx = worldToTex(x, S);
    const cy = worldToTex(z, S);

    // recessed shadow
    const g = ctx.createRadialGradient(cx, cy, holeR * 0.1, cx, cy, holeR);
    g.addColorStop(0, 'rgba(0,0,0,0.92)');
    g.addColorStop(0.55, 'rgba(0,0,0,0.78)');
    g.addColorStop(0.92, 'rgba(0,0,0,0.25)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, holeR, 0, Math.PI * 2);
    ctx.fill();

    // light catch on the lower rim (drilled edge highlight)
    ctx.strokeStyle = 'rgba(255,255,255,0.10)';
    ctx.lineWidth = holeR * 0.14;
    ctx.beginPath();
    ctx.arc(cx, cy, holeR * 0.94, Math.PI * 0.15, Math.PI * 0.85);
    ctx.stroke();
  }

  // ── Soft vignette toward the wooden frame ──
  const vg = ctx.createRadialGradient(S / 2, S / 2, S * 0.3, S / 2, S / 2, S * 0.52);
  vg.addColorStop(0, 'rgba(0,0,0,0)');
  vg.addColorStop(0.8, hexToRgba(deep, 0.18));
  vg.addColorStop(1, hexToRgba(deep, 0.45));
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, S, S);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

function hexToRgba(c: { r: number; g: number; b: number }, a: number): string {
  return `rgba(${Math.round(c.r * 255)},${Math.round(c.g * 255)},${Math.round(c.b * 255)},${a})`;
}

/** Bake oak/walnut grain with hand-hewn streaks and a pair of knots. */
export function createWoodTexture(theme: ThemePreset): {
  map: THREE.CanvasTexture;
  bump: THREE.CanvasTexture;
} {
  const { canvas, ctx } = makeCanvas(WOOD_TEX_SIZE);
  const S = WOOD_TEX_SIZE;

  const grad = ctx.createLinearGradient(0, 0, 0, S);
  grad.addColorStop(0, theme.wood);
  grad.addColorStop(0.5, theme.woodDeep);
  grad.addColorStop(1, theme.wood);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, S, S);

  // long grain streaks
  for (let i = 0; i < 170; i++) {
    const y = Math.random() * S;
    const amp = 2 + Math.random() * 7;
    const period = 120 + Math.random() * 420;
    const phase = Math.random() * Math.PI * 2;
    const light = Math.random() > 0.5;
    ctx.strokeStyle = light
      ? `rgba(255,238,210,${0.03 + Math.random() * 0.07})`
      : `rgba(30,18,8,${0.04 + Math.random() * 0.09})`;
    ctx.lineWidth = 0.6 + Math.random() * 2.4;
    ctx.beginPath();
    for (let x = 0; x <= S; x += 8) {
      const yy = y + Math.sin((x / period) * Math.PI * 2 + phase) * amp;
      if (x === 0) ctx.moveTo(x, yy);
      else ctx.lineTo(x, yy);
    }
    ctx.stroke();
  }

  // two knots (节疤)
  for (let k = 0; k < 2; k++) {
    const kx = Math.random() * S;
    const ky = Math.random() * S;
    for (let r = 34; r > 2; r -= 3.4) {
      ctx.strokeStyle = `rgba(28,16,8,${0.05 + (34 - r) * 0.004})`;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.ellipse(kx, ky, r * 1.6, r, 0.12, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  // fine noise
  const img = ctx.getImageData(0, 0, S, S);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() - 0.5) * 14;
    d[i] = (d[i] ?? 0) + n;
    d[i + 1] = (d[i + 1] ?? 0) + n;
    d[i + 2] = (d[i + 2] ?? 0) + n;
  }
  ctx.putImageData(img, 0, 0);

  const map = new THREE.CanvasTexture(canvas);
  map.colorSpace = THREE.SRGBColorSpace;
  map.wrapS = THREE.RepeatWrapping;
  map.wrapT = THREE.RepeatWrapping;
  map.rotation = (Math.floor(Math.random() * 4) * Math.PI) / 2; // 质感律：木纹随机走向
  map.anisotropy = 8;

  const bump = new THREE.CanvasTexture(canvas);
  bump.wrapS = THREE.RepeatWrapping;
  bump.wrapT = THREE.RepeatWrapping;
  return { map, bump };
}

/** Subtle felt weave bump used on the playfield. */
export function createFeltBump(): THREE.CanvasTexture {
  const size = 512;
  const { canvas, ctx } = makeCanvas(size);
  ctx.fillStyle = '#808080';
  ctx.fillRect(0, 0, size, size);
  for (let i = 0; i < 26000; i++) {
    const v = Math.floor(Math.random() * 255);
    ctx.fillStyle = `rgba(${v},${v},${v},0.35)`;
    ctx.fillRect(Math.random() * size, Math.random() * size, 1.5, 1.5);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(6, 6);
  return tex;
}
